import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DownloadSimple,
    UploadSimple,
    FloppyDisk,
    ArrowCounterClockwise,
    Trash,
    Database,
    Clock,
    PlayCircle,
    CalendarBlank,
    CurrencyEur,
    Palette,
    Check,
    ListBullets,
    MagnifyingGlass,
    Key,
    Warning,
    HardDrives,
    ClockCounterClockwise,
    Broom,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ConfirmCodeDialog from "@/components/ConfirmCodeDialog";
import { THEMES, getStoredTheme, applyTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth-context";
import PosSettingsSection from "@/components/PosSettingsSection";

const COLLECTIONS = [
    { key: "customers", label: "ΠΕΛΑΤΕΣ" },
    { key: "items", label: "ΕΙΔΗ" },
    { key: "sales", label: "ΠΩΛΗΣΕΙΣ" },
    { key: "transactions", label: "ΚΙΝΗΣΕΙΣ" },
    { key: "parked_sales", label: "ΑΝΑΣΤΟΛΕΣ" },
];

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleString("el-GR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "—";

const fmtBytes = (n) => {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

function downloadBlob(data, filename, mime) {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 1000);
}

function rowsToCSV(rows) {
    if (!rows || rows.length === 0) return "";
    const keys = Array.from(
        rows.reduce((set, r) => {
            Object.keys(r).forEach((k) => set.add(k));
            return set;
        }, new Set())
    );
    const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };
    const lines = [keys.join(",")];
    for (const r of rows) {
        lines.push(keys.map((k) => escape(r[k])).join(","));
    }
    return lines.join("\n");
}

function csvToRows(text) {
    // Lightweight CSV parser handling quotes
    const rows = [];
    let i = 0,
        cur = [],
        cell = "",
        inQ = false;
    while (i < text.length) {
        const ch = text[i];
        if (inQ) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    cell += '"';
                    i += 2;
                    continue;
                }
                inQ = false;
                i++;
                continue;
            }
            cell += ch;
            i++;
            continue;
        }
        if (ch === '"') {
            inQ = true;
            i++;
            continue;
        }
        if (ch === ",") {
            cur.push(cell);
            cell = "";
            i++;
            continue;
        }
        if (ch === "\n") {
            cur.push(cell);
            rows.push(cur);
            cur = [];
            cell = "";
            i++;
            continue;
        }
        if (ch === "\r") {
            i++;
            continue;
        }
        cell += ch;
        i++;
    }
    if (cell || cur.length) {
        cur.push(cell);
        rows.push(cur);
    }
    if (rows.length === 0) return [];
    const keys = rows[0];
    return rows.slice(1).map((r) => {
        const o = {};
        keys.forEach((k, idx) => (o[k] = r[idx] ?? ""));
        return o;
    });
}

export default function Backup() {
    const [activeTab, setActiveTab] = useState("limits");
    const { user } = useAuth();
    const isSupervisor = !!user?.is_supervisor;

    return (
        <div
            className="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 max-w-6xl w-full"
            data-testid="backup-page"
        >
            <div className="mb-8 fade-up">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2">
                    ΣΥΣΤΗΜΑ
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
                    ΑΣΦΑΛΕΙΑ
                </h1>
                <p className="mt-2 text-base text-[#5C5F66]">
                    Διαχείριση εφεδρικών αντιγράφων, εισαγωγή / εξαγωγή πινάκων.
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="rounded-none bg-white border-2 border-[#0A0A0A] p-0 h-auto">
                    <TabsTrigger
                        value="collections"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-collections"
                    >
                        ΠΙΝΑΚΕΣ (CSV / EXCEL)
                    </TabsTrigger>
                    <TabsTrigger
                        value="auto"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-auto"
                    >
                        ΑΥΤΟΜΑΤΟ BACKUP
                    </TabsTrigger>
                    <TabsTrigger
                        value="limits"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-monthly-limits"
                    >
                        ΜΗΝΙΑΙΟ ΟΡΙΟ
                    </TabsTrigger>
                    <TabsTrigger
                        value="theme"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-theme"
                    >
                        ΕΜΦΑΝΙΣΗ
                    </TabsTrigger>
                    <TabsTrigger
                        value="users"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-users"
                    >
                        ΧΡΗΣΤΕΣ
                    </TabsTrigger>
                    <TabsTrigger
                        value="logs"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-logs"
                    >
                        LOGS
                    </TabsTrigger>
                    {isSupervisor && (
                        <TabsTrigger
                            value="license"
                            className="rounded-none data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                            data-testid="tab-license"
                        >
                            ΑΔΕΙΑ
                        </TabsTrigger>
                    )}
                    {isSupervisor && (
                        <TabsTrigger
                            value="db"
                            className="rounded-none data-[state=active]:bg-[#5856D6] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                            data-testid="tab-db-stats"
                        >
                            ΒΑΣΗ
                        </TabsTrigger>
                    )}
                    <TabsTrigger
                        value="settings"
                        className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]"
                        data-testid="tab-pos-settings"
                    >
                        ΡΥΘΜΙΣΕΙΣ
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="collections" className="mt-6">
                    <CollectionsSection />
                </TabsContent>
                <TabsContent value="auto" className="mt-6">
                    <AutoBackupSection />
                </TabsContent>
                <TabsContent value="limits" className="mt-6">
                    <MonthlyLimitsSection />
                </TabsContent>
                <TabsContent value="theme" className="mt-6">
                    <ThemeSection />
                </TabsContent>
                <TabsContent value="users" className="mt-6">
                    <UsersSection />
                </TabsContent>
                <TabsContent value="logs" className="mt-6">
                    <LogsSection />
                </TabsContent>
                {isSupervisor && (
                    <TabsContent value="license" className="mt-6">
                        <LicenseSection />
                    </TabsContent>
                )}
                {isSupervisor && (
                    <TabsContent value="db" className="mt-6">
                        <DbStatsSection />
                    </TabsContent>
                )}
                <TabsContent value="settings" className="mt-6">
                    <PosSettingsSection />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function CollectionsSection() {
    const [busy, setBusy] = useState(null);
    const [pending, setPending] = useState(null); // { file, key, label, replace }
    const fileRefs = useRef({});

    const exportCSV = async (key, label) => {
        setBusy(`${key}_csv`);
        try {
            const { data } = await api.get(`/backup/collection/${key}`);
            const csv = rowsToCSV(data.rows);
            downloadBlob(csv, `${key}.csv`, "text/csv;charset=utf-8");
            toast.success(`${label}: ${data.count} γραμμές → CSV`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα export");
        } finally {
            setBusy(null);
        }
    };

    const exportXLSX = async (key, label) => {
        setBusy(`${key}_xlsx`);
        try {
            const { data } = await api.get(`/backup/collection/${key}`);
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(data.rows);
            XLSX.utils.book_append_sheet(wb, ws, key);
            // Use write+blob (cross-browser) instead of writeFile
            const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([buf], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            downloadBlob(
                blob,
                `${key}.xlsx`,
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            );
            toast.success(`${label}: ${data.count} γραμμές → Excel`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα export");
        } finally {
            setBusy(null);
        }
    };

    const onFileChosen = (key, label, file, replace) => {
        if (!file) return;
        // Open confirm dialog before doing anything destructive/import
        setPending({ key, label, file, replace });
    };

    const performImport = async (confirm_code) => {
        if (!pending) return;
        const { key, label, file, replace } = pending;
        setBusy(`${key}_import`);
        setPending(null);
        try {
            let rows = [];
            const name = file.name.toLowerCase();
            if (name.endsWith(".csv")) {
                rows = csvToRows(await file.text());
            } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
                const buf = await file.arrayBuffer();
                const wb = XLSX.read(buf, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
            } else if (name.endsWith(".json")) {
                rows = JSON.parse(await file.text());
            } else {
                toast.error("Υποστηρίζονται: .csv .xlsx .json");
                return;
            }
            if (!Array.isArray(rows) || rows.length === 0) {
                toast.error("Το αρχείο δεν περιέχει γραμμές");
                return;
            }
            const { data } = await api.post(`/backup/collection/${key}`, {
                rows,
                replace,
                confirm_code,
            });
            toast.success(
                `${label}: εισήχθησαν ${data.imported} γραμμές${replace ? " (αντικατάσταση)" : ""}`
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα import");
        } finally {
            setBusy(null);
            if (fileRefs.current[key]) fileRefs.current[key].value = "";
        }
    };

    return (
        <div className="space-y-3" data-testid="collections-section">
            {COLLECTIONS.map(({ key, label }) => (
                <div
                    key={key}
                    className="border-2 border-[#0A0A0A] bg-white p-4 flex items-center gap-4 flex-wrap"
                    data-testid={`collection-row-${key}`}
                >
                    <div className="flex items-center gap-2 min-w-[180px]">
                        <Database size={18} weight="bold" />
                        <span className="font-heading font-black text-lg tracking-tighter">
                            {label}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            onClick={() => exportCSV(key, label)}
                            disabled={busy === `${key}_csv`}
                            data-testid={`exp-csv-${key}`}
                            className="rounded-none h-10 bg-white border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold"
                        >
                            <DownloadSimple size={14} weight="bold" className="mr-1" />
                            CSV
                        </Button>
                        <Button
                            onClick={() => exportXLSX(key, label)}
                            disabled={busy === `${key}_xlsx`}
                            data-testid={`exp-xlsx-${key}`}
                            className="rounded-none h-10 bg-white border-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-bold"
                        >
                            <DownloadSimple size={14} weight="bold" className="mr-1" />
                            EXCEL
                        </Button>
                        <input
                            type="file"
                            ref={(el) => (fileRefs.current[key] = el)}
                            accept=".csv,.xlsx,.xls,.json"
                            data-testid={`file-input-${key}`}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                const replace = e.target.dataset.replace === "1";
                                onFileChosen(key, label, f, replace);
                            }}
                            className="hidden"
                        />
                        <Button
                            onClick={() => {
                                fileRefs.current[key].dataset.replace = "0";
                                fileRefs.current[key]?.click();
                            }}
                            disabled={busy === `${key}_import`}
                            data-testid={`imp-merge-${key}`}
                            className="rounded-none h-10 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-bold"
                        >
                            <UploadSimple size={14} weight="bold" className="mr-1" />
                            ΕΙΣΑΓΩΓΗ (MERGE)
                        </Button>
                        <Button
                            onClick={() => {
                                fileRefs.current[key].dataset.replace = "1";
                                fileRefs.current[key]?.click();
                            }}
                            disabled={busy === `${key}_import`}
                            data-testid={`imp-replace-${key}`}
                            className="rounded-none h-10 bg-white border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold"
                        >
                            ΑΝΤΙΚΑΤΑΣΤΑΣΗ
                        </Button>
                    </div>
                </div>
            ))}
            <p className="text-xs text-[#5C5F66] mt-2">
                Υποστηριζόμενες μορφές import: <strong>.csv</strong>, <strong>.xlsx</strong>,{" "}
                <strong>.json</strong>. Στο MERGE, οι γραμμές με ίδιο <code>id</code>{" "}
                ενημερώνονται. Κάθε import απαιτεί κωδικό επιβεβαίωσης.
            </p>

            <ConfirmCodeDialog
                open={!!pending}
                title={
                    pending?.replace
                        ? `ΑΝΤΙΚΑΤΑΣΤΑΣΗ ΠΙΝΑΚΑ ${pending?.label || ""}`
                        : `ΕΙΣΑΓΩΓΗ ΣΤΟΝ ΠΙΝΑΚΑ ${pending?.label || ""}`
                }
                description={
                    pending?.replace
                        ? "Θα διαγραφούν όλα τα δεδομένα του πίνακα και θα αντικατασταθούν."
                        : "Οι νέες γραμμές θα προστεθούν, οι υπάρχουσες με ίδιο id θα ενημερωθούν."
                }
                onConfirm={performImport}
                onClose={() => {
                    setPending(null);
                    if (pending && fileRefs.current[pending.key]) {
                        fileRefs.current[pending.key].value = "";
                    }
                }}
            />
        </div>
    );
}

function AutoBackupSection() {
    const [settings, setSettings] = useState(null);
    const [list, setList] = useState([]);
    const [running, setRunning] = useState(false);
    const [restoreId, setRestoreId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        try {
            const [s, l] = await Promise.all([
                api.get("/backup/settings"),
                api.get("/backup/list"),
            ]);
            setSettings(s.data);
            setList(l.data);
        } catch {
            toast.error("Αποτυχία φόρτωσης");
        }
    };

    useEffect(() => {
        load();
    }, []);

    const saveSettings = async (next) => {
        try {
            const { data } = await api.put("/backup/settings", next);
            setSettings(data);
            toast.success("Αποθηκεύτηκαν οι ρυθμίσεις");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αποθήκευσης");
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            await api.post("/backup/run");
            toast.success("Δημιουργήθηκε νέο backup");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα");
        } finally {
            setRunning(false);
        }
    };

    const downloadBackup = async (id, created_at) => {
        try {
            const { data } = await api.get(`/backup/${id}`);
            const ts = (created_at || "").slice(0, 19).replace(/[:T]/g, "-");
            downloadBlob(
                JSON.stringify(data, null, 2),
                `backup-${ts}.json`,
                "application/json"
            );
        } catch {
            toast.error("Σφάλμα κατεβάσματος");
        }
    };

    const deleteBackup = (id) => {
        setDeleteId(id);
    };

    const performDelete = async (confirm_code) => {
        const id = deleteId;
        setDeleteId(null);
        if (!id) return;
        try {
            await api.delete(`/backup/${id}`, { data: { confirm_code } });
            toast.success("Το backup διαγράφηκε");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα διαγραφής");
        }
    };

    const restoreBackup = (id) => {
        setRestoreId(id);
    };

    const performRestore = async (confirm_code) => {
        const id = restoreId;
        setRestoreId(null);
        if (!id) return;
        try {
            const { data } = await api.post(`/backup/${id}/restore`, {
                confirm_code,
            });
            toast.success(
                `Επαναφορά: ${Object.entries(data.restored)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(", ")}`
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα επαναφοράς");
        }
    };

    if (!settings) {
        return <div className="text-sm text-[#5C5F66]">Φόρτωση...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Settings card */}
            <div
                className="border-2 border-[#0A0A0A] bg-white p-5"
                data-testid="auto-backup-settings"
            >
                <div className="flex items-start justify-between gap-5 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                        <div className="flex items-center gap-3 mb-1">
                            <Clock size={20} weight="bold" />
                            <span className="font-heading text-2xl font-black tracking-tighter">
                                ΑΥΤΟΜΑΤΟ BACKUP
                            </span>
                        </div>
                        <p className="text-sm text-[#5C5F66]">
                            Ενεργοποιήστε για περιοδική δημιουργία backup. Τελευταίο τρέξιμο:{" "}
                            <strong>{fmtDate(settings.last_run)}</strong>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Label className="text-sm font-bold uppercase tracking-wider">
                            ΕΝΕΡΓΟ
                        </Label>
                        <Switch
                            checked={settings.enabled}
                            onCheckedChange={(v) =>
                                saveSettings({ ...settings, enabled: v })
                            }
                            data-testid="autobackup-enable"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                            ΣΥΧΝΟΤΗΤΑ
                        </Label>
                        <Select
                            value={settings.frequency}
                            onValueChange={(v) =>
                                saveSettings({ ...settings, frequency: v })
                            }
                        >
                            <SelectTrigger
                                data-testid="autobackup-frequency"
                                className="rounded-none h-12 border-[#0A0A0A] border-2"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="daily">ΚΑΘΗΜΕΡΙΝΑ</SelectItem>
                                <SelectItem value="weekly">ΕΒΔΟΜΑΔΙΑΙΑ</SelectItem>
                                <SelectItem value="monthly">ΜΗΝΙΑΙΑ</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                            ΔΙΑΤΗΡΗΣΗ ΑΝΤΙΓΡΑΦΩΝ
                        </Label>
                        <Input
                            type="number"
                            min={1}
                            max={365}
                            value={settings.keep}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    keep: parseInt(e.target.value || "1", 10),
                                })
                            }
                            onBlur={() => saveSettings(settings)}
                            data-testid="autobackup-keep"
                            className="rounded-none h-12 border-[#0A0A0A] border-2 font-mono text-base"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={runNow}
                            disabled={running}
                            data-testid="run-backup-now"
                            className="w-full rounded-none h-12 bg-[#002FA7] hover:bg-[#001D66] text-white font-black tracking-wider"
                        >
                            <PlayCircle size={18} weight="bold" className="mr-2" />
                            ΔΗΜΙΟΥΡΓΙΑ ΤΩΡΑ
                        </Button>
                    </div>
                </div>
            </div>

            {/* Backups list */}
            <div
                className="border-2 border-[#0A0A0A] bg-white"
                data-testid="backups-list"
            >
                <div className="px-5 py-3 border-b-2 border-[#0A0A0A] flex items-center gap-2">
                    <FloppyDisk size={18} weight="bold" />
                    <span className="font-heading text-xl font-black tracking-tighter">
                        ΑΠΟΘΗΚΕΥΜΕΝΑ BACKUP
                    </span>
                    <span className="text-xs font-mono text-[#5C5F66] ml-2">
                        ({list.length})
                    </span>
                </div>
                {list.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5C5F66]">
                        Δεν υπάρχουν ακόμη αποθηκευμένα backup.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#0A0A0A] hover:bg-[#0A0A0A]">
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Ημερομηνια
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Τυπος
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Μεγεθος
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Περιεχομενα
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                    Ενεργειες
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {list.map((b) => (
                                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                                    <TableCell className="font-mono text-xs">
                                        {fmtDate(b.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${
                                                b.kind === "auto"
                                                    ? "bg-[#FFF8DD] text-[#7A5C00] border border-[#FFCC00]"
                                                    : "bg-[#EEF1FB] text-[#002FA7] border border-[#002FA7]"
                                            }`}
                                        >
                                            {b.kind === "auto" ? "ΑΥΤΟΜΑΤΟ" : "ΧΕΙΡΟΚΙΝΗΤΟ"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        <div>{fmtBytes(b.size_bytes)}</div>
                                        {b.raw_size_bytes &&
                                            b.raw_size_bytes > b.size_bytes && (
                                                <div
                                                    className="text-[10px] text-[#1F8A3E] font-bold"
                                                    title={`Ασυμπίεστο: ${fmtBytes(b.raw_size_bytes)}`}
                                                >
                                                    ↓ {(b.raw_size_bytes / b.size_bytes).toFixed(1)}× compressed
                                                </div>
                                            )}
                                    </TableCell>
                                    <TableCell className="text-xs text-[#5C5F66]">
                                        {b.counts &&
                                            `${b.counts.customers || 0} πελ. · ${b.counts.items || 0} είδη · ${b.counts.sales || 0} πωλ. · ${b.counts.transactions || 0} κιν.`}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                                        <Button
                                            onClick={() =>
                                                downloadBackup(b.id, b.created_at)
                                            }
                                            data-testid={`download-${b.id}`}
                                            className="rounded-none h-9 bg-white border border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold text-xs"
                                        >
                                            <DownloadSimple
                                                size={12}
                                                weight="bold"
                                                className="mr-1"
                                            />
                                            ΛΗΨΗ
                                        </Button>
                                        <Button
                                            onClick={() => restoreBackup(b.id)}
                                            data-testid={`restore-${b.id}`}
                                            className="rounded-none h-9 bg-white border border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500] hover:text-white font-bold text-xs"
                                        >
                                            <ArrowCounterClockwise
                                                size={12}
                                                weight="bold"
                                                className="mr-1"
                                            />
                                            ΕΠΑΝΑΦΟΡΑ
                                        </Button>
                                        <Button
                                            onClick={() => deleteBackup(b.id)}
                                            data-testid={`delete-${b.id}`}
                                            className="rounded-none h-9 bg-white border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs"
                                        >
                                            <Trash size={12} weight="bold" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <ConfirmCodeDialog
                open={!!restoreId}
                title="ΕΠΑΝΑΦΟΡΑ ΑΠΟ BACKUP"
                description="Όλα τα τρέχοντα δεδομένα θα ΑΝΤΙΚΑΤΑΣΤΑΘΟΥΝ από αυτό το backup. Εισαγάγετε τον κωδικό επιβεβαίωσης για να συνεχίσετε."
                onConfirm={performRestore}
                onClose={() => setRestoreId(null)}
            />
            <ConfirmCodeDialog
                open={!!deleteId}
                title="ΔΙΑΓΡΑΦΗ BACKUP"
                description="Πρόκειται να διαγράψετε ΟΡΙΣΤΙΚΑ αυτό το backup. Η ενέργεια δεν αναιρείται. Εισαγάγετε τον κωδικό επιβεβαίωσης για να συνεχίσετε."
                onConfirm={performDelete}
                onClose={() => setDeleteId(null)}
            />
        </div>
    );
}


// ===== Monthly Limits Section =====
const monthLabel = (key) => {
    if (!key || typeof key !== "string" || !key.match(/^\d{4}-\d{2}$/)) return key;
    const [y, m] = key.split("-");
    const months = [
        "ΙΑΝ",
        "ΦΕΒ",
        "ΜΑΡ",
        "ΑΠΡ",
        "ΜΑΪ",
        "ΙΟΥΝ",
        "ΙΟΥΛ",
        "ΑΥΓ",
        "ΣΕΠ",
        "ΟΚΤ",
        "ΝΟΕ",
        "ΔΕΚ",
    ];
    const idx = parseInt(m, 10) - 1;
    return `${months[idx] || m} ${y}`;
};

const fmtEur = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return "—";
    return `${n.toFixed(2).replace(".", ",")} €`;
};

function MonthlyLimitsSection() {
    const [list, setList] = useState([]);
    const [current, setCurrent] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form state
    const [monthKey, setMonthKey] = useState("");
    const [amount, setAmount] = useState("25");

    const [pendingSave, setPendingSave] = useState(null); // {month_key, amount}
    const [pendingDelete, setPendingDelete] = useState(null); // month_key
    const [editTarget, setEditTarget] = useState(null); // {month_key, amount}

    const load = async () => {
        setLoading(true);
        try {
            const [l, c] = await Promise.all([
                api.get("/monthly-limits"),
                api.get("/monthly-limits/current"),
            ]);
            setList(l.data);
            setCurrent(c.data);
        } catch {
            toast.error("Αποτυχία φόρτωσης");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Build month dropdown options: always show next 12 unsaved future months
    // (auto-extends as months get saved so the user always has 12 to choose from)
    const monthOptions = useMemo(() => {
        const used = new Set(list.map((l) => l.month_key));
        const opts = [];
        const now = new Date();
        // Optionally include previous month if not already saved
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevK = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
        if (!used.has(prevK)) {
            opts.push({ key: prevK, label: monthLabel(prevK) });
        }
        // Walk forward until we have 12 unsaved options total (cap 60 to avoid runaway)
        let i = 0;
        while (opts.length < 12 && i < 60) {
            const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            if (!used.has(k)) opts.push({ key: k, label: monthLabel(k) });
            i++;
        }
        return opts;
    }, [list]);

    // Keep selected month valid (auto-select first available)
    useEffect(() => {
        if (monthOptions.length === 0) {
            setMonthKey("");
        } else if (!monthOptions.some((o) => o.key === monthKey)) {
            setMonthKey(monthOptions[0].key);
        }
    }, [monthOptions, monthKey]);

    const onSaveClick = () => {
        const amt = parseFloat(String(amount).replace(",", "."));
        if (!monthKey || !monthKey.match(/^\d{4}-\d{2}$/)) {
            toast.error("Επιλέξτε έναν διαθέσιμο μήνα");
            return;
        }
        if (isNaN(amt) || amt <= 0) {
            toast.error("Το ποσό πρέπει να είναι μεγαλύτερο από 0");
            return;
        }
        setPendingSave({ month_key: monthKey, amount: amt });
    };

    const performSave = async (confirm_code) => {
        if (!pendingSave) return;
        const { month_key, amount } = pendingSave;
        setPendingSave(null);
        try {
            await api.post("/monthly-limits", { month_key, amount, confirm_code });
            toast.success(
                `Αποθηκεύτηκε όριο ${fmtEur(amount)} για ${monthLabel(month_key)}`
            );
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αποθήκευσης");
        }
    };

    const performDelete = async (confirm_code) => {
        const month_key = pendingDelete;
        setPendingDelete(null);
        if (!month_key) return;
        try {
            await api.delete(`/monthly-limits/${month_key}`, {
                data: { confirm_code },
            });
            toast.success(`Διαγράφηκε όριο για ${monthLabel(month_key)}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα διαγραφής");
        }
    };

    const performEdit = async (newAmount, confirm_code) => {
        if (!editTarget) return;
        const { month_key } = editTarget;
        try {
            await api.put(`/monthly-limits/${month_key}`, {
                amount: newAmount,
                confirm_code,
            });
            toast.success(
                `Ενημερώθηκε όριο ${fmtEur(newAmount)} για ${monthLabel(month_key)}`
            );
            setEditTarget(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα ενημέρωσης");
        }
    };

    return (
        <div className="space-y-6" data-testid="monthly-limits-section">
            {/* Current month status banner */}
            {current && (
                <div
                    className={`border-2 p-5 ${
                        current.is_set
                            ? "border-[#34C759] bg-[#EAF7EE]"
                            : "border-[#FF3B30] bg-[#FDEAE8]"
                    }`}
                    data-testid="current-month-status"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <CalendarBlank size={26} weight="bold" />
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                                    ΤΡΕΧΩΝ ΜΗΝΑΣ
                                </div>
                                <div className="font-heading text-3xl font-black tracking-tighter">
                                    {monthLabel(current.month_key)}
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                                ΟΡΙΟ ΠΙΣΤΩΣΗΣ
                            </div>
                            {current.is_set ? (
                                <div className="font-heading text-4xl font-black tracking-tighter text-[#1F8A3E]">
                                    {fmtEur(current.amount)}
                                </div>
                            ) : (
                                <div className="font-heading text-2xl font-black tracking-tighter text-[#B11919] uppercase">
                                    ΔΕΝ ΕΧΕΙ ΟΡΙΣΤΕΙ
                                </div>
                            )}
                        </div>
                    </div>
                    {!current.is_set && (
                        <div className="mt-3 text-sm text-[#B11919]">
                            ⚠ Καμία πώληση δεν επιτρέπεται μέχρι να ορίσετε όριο για αυτόν τον μήνα.
                        </div>
                    )}
                </div>
            )}

            {/* Set limit form */}
            <div
                className="border-2 border-[#0A0A0A] bg-white p-5"
                data-testid="set-limit-form"
            >
                <div className="flex items-center gap-3 mb-4">
                    <CurrencyEur size={22} weight="bold" />
                    <span className="font-heading text-2xl font-black tracking-tighter">
                        ΟΡΙΣΜΟΣ / ΤΡΟΠΟΠΟΙΗΣΗ ΟΡΙΟΥ
                    </span>
                </div>
                <p className="text-sm text-[#5C5F66] mb-4">
                    Καθορίστε το μηνιαίο ποσό πίστωσης που ισχύει για ΟΛΟΥΣ τους πελάτες
                    για τον επιλεγμένο μήνα. Απαιτείται κωδικός επιβεβαίωσης.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                            ΜΗΝΑΣ
                        </Label>
                        <Select
                            value={monthKey}
                            onValueChange={setMonthKey}
                            disabled={monthOptions.length === 0}
                        >
                            <SelectTrigger
                                data-testid="ml-month-select"
                                className="rounded-none h-12 border-[#0A0A0A] border-2 font-mono text-base"
                            >
                                <SelectValue
                                    placeholder={
                                        monthOptions.length === 0
                                            ? "ΟΛΟΙ ΟΙ ΜΗΝΕΣ ΕΧΟΥΝ ΗΔΗ ΟΡΙΟ"
                                            : "Επιλέξτε μήνα"
                                    }
                                >
                                    {monthKey ? monthLabel(monthKey) : ""}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="rounded-none max-h-72">
                                {monthOptions.map((o) => (
                                    <SelectItem
                                        key={o.key}
                                        value={o.key}
                                        data-testid={`ml-month-option-${o.key}`}
                                    >
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                            ΠΟΣΟ €
                        </Label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="25,00"
                            data-testid="ml-amount-input"
                            className="rounded-none h-12 border-[#0A0A0A] border-2 font-mono text-base"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={onSaveClick}
                            disabled={monthOptions.length === 0}
                            data-testid="ml-save-btn"
                            className="w-full rounded-none h-12 bg-[#002FA7] hover:bg-[#001D66] text-white font-black tracking-wider disabled:opacity-50"
                        >
                            <FloppyDisk size={18} weight="bold" className="mr-2" />
                            ΑΠΟΘΗΚΕΥΣΗ
                        </Button>
                    </div>
                </div>
                {monthOptions.length === 0 && (
                    <p className="text-xs text-[#5C5F66] mt-3">
                        Όλοι οι διαθέσιμοι μήνες έχουν ήδη όριο. Χρησιμοποιήστε το κουμπί ΕΠΕΞΕΡΓΑΣΙΑ στη λίστα παρακάτω για να αλλάξετε υπάρχον ποσό.
                    </p>
                )}
            </div>

            {/* List of saved monthly limits */}
            <div
                className="border-2 border-[#0A0A0A] bg-white"
                data-testid="ml-list"
            >
                <div className="px-5 py-3 border-b-2 border-[#0A0A0A] flex items-center gap-2">
                    <CalendarBlank size={18} weight="bold" />
                    <span className="font-heading text-xl font-black tracking-tighter">
                        ΑΠΟΘΗΚΕΥΜΕΝΑ ΟΡΙΑ ΜΗΝΩΝ
                    </span>
                    <span className="text-xs font-mono text-[#5C5F66] ml-2">
                        ({list.length})
                    </span>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#5C5F66]">
                        Φόρτωση...
                    </div>
                ) : list.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5C5F66]">
                        Δεν έχει οριστεί όριο για κανέναν μήνα ακόμη.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#0A0A0A] hover:bg-[#0A0A0A]">
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Μηνας
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                    Ποσο
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                    Ορισμενο
                                </TableHead>
                                <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                    Ενεργειες
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {list.map((l) => {
                                const isCurrent =
                                    current && current.month_key === l.month_key;
                                return (
                                    <TableRow
                                        key={l.month_key}
                                        data-testid={`ml-row-${l.month_key}`}
                                    >
                                        <TableCell className="font-bold">
                                            {monthLabel(l.month_key)}
                                            {isCurrent && (
                                                <span className="ml-2 text-[10px] font-bold uppercase bg-[#002FA7] text-white px-1.5 py-0.5">
                                                    ΤΡΕΧΩΝ
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-bold">
                                            {fmtEur(l.amount)}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-[#5C5F66]">
                                            {fmtDate(l.set_at)}
                                        </TableCell>
                                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                                            <Button
                                                onClick={() => setEditTarget(l)}
                                                data-testid={`ml-edit-${l.month_key}`}
                                                className="rounded-none h-9 bg-white border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-bold text-xs"
                                            >
                                                ΕΠΕΞΕΡΓΑΣΙΑ
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    setPendingDelete(l.month_key)
                                                }
                                                data-testid={`ml-delete-${l.month_key}`}
                                                className="rounded-none h-9 bg-white border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs"
                                            >
                                                <Trash size={12} weight="bold" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </div>

            <EditMonthlyLimitDialog
                target={editTarget}
                onConfirm={performEdit}
                onClose={() => setEditTarget(null)}
            />
            <ConfirmCodeDialog
                open={!!pendingSave}
                title={`ΑΠΟΘΗΚΕΥΣΗ ΟΡΙΟΥ ${monthLabel(pendingSave?.month_key)}`}
                description={`Θα οριστεί όριο πίστωσης ${fmtEur(pendingSave?.amount)} για όλους τους πελάτες για τον μήνα ${monthLabel(pendingSave?.month_key)}.`}
                onConfirm={performSave}
                onClose={() => setPendingSave(null)}
            />
            <ConfirmCodeDialog
                open={!!pendingDelete}
                title={`ΔΙΑΓΡΑΦΗ ΟΡΙΟΥ ${monthLabel(pendingDelete)}`}
                description={`Θα διαγραφεί το μηνιαίο όριο για τον μήνα ${monthLabel(pendingDelete)}. Καμία αγορά δεν θα μπορεί να γίνει για αυτόν τον μήνα.`}
                onConfirm={performDelete}
                onClose={() => setPendingDelete(null)}
            />
        </div>
    );
}

function EditMonthlyLimitDialog({ target, onConfirm, onClose }) {
    const [amount, setAmount] = useState("");
    const [code, setCode] = useState("");

    useEffect(() => {
        if (target) {
            setAmount(String(target.amount).replace(".", ","));
            setCode("");
        }
    }, [target]);

    const submit = (e) => {
        e?.preventDefault();
        const amt = parseFloat(String(amount).replace(",", "."));
        if (isNaN(amt) || amt <= 0) {
            toast.error("Το ποσό πρέπει να είναι μεγαλύτερο από 0");
            return;
        }
        if (!code) {
            toast.error("Εισαγάγετε κωδικό επιβεβαίωσης");
            return;
        }
        onConfirm(amt, code);
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="rounded-none max-w-md border-2 border-[#002FA7]"
                data-testid="edit-limit-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                        ΕΠΕΞΕΡΓΑΣΙΑ ΟΡΙΟΥ {target ? monthLabel(target.month_key) : ""}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-[#5C5F66]">
                        Αλλάξτε το ποσό που ισχύει για όλους τους πελάτες αυτόν τον μήνα. Απαιτείται κωδικός επιβεβαίωσης.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                            ΝΕΟ ΠΟΣΟ €
                        </Label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            autoFocus
                            data-testid="ml-edit-amount"
                            className="rounded-none h-12 border-2 border-[#0A0A0A] text-base font-mono"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                            ΚΩΔΙΚΟΣ ΕΠΙΒΕΒΑΙΩΣΗΣ
                        </Label>
                        <Input
                            type="password"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            data-testid="ml-edit-code"
                            className="rounded-none h-12 border-2 border-[#FF3B30] text-base font-mono"
                            placeholder="••••"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="outline"
                            data-testid="ml-edit-cancel"
                            className="rounded-none h-11 border-2 border-[#0A0A0A] font-bold"
                        >
                            ΑΚΥΡΩΣΗ
                        </Button>
                        <Button
                            type="submit"
                            disabled={!code || !amount}
                            data-testid="ml-edit-save"
                            className="rounded-none h-11 bg-[#002FA7] hover:bg-[#001D66] text-white font-black tracking-wider"
                        >
                            ΑΠΟΘΗΚΕΥΣΗ
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


// ===== Theme Section =====
function ThemeSection() {
    const [active, setActive] = useState(getStoredTheme());

    const choose = (key) => {
        setActive(key);
        applyTheme(key);
        const t = THEMES.find((x) => x.key === key);
        toast.success(`Θέμα: ${t?.label || key}`);
    };

    return (
        <div className="space-y-6" data-testid="theme-section">
            <div className="border-2 border-[#0A0A0A] bg-white p-5">
                <div className="flex items-center gap-3 mb-2">
                    <Palette size={22} weight="bold" />
                    <span className="font-heading text-2xl font-black tracking-tighter">
                        ΘΕΜΑ ΕΦΑΡΜΟΓΗΣ
                    </span>
                </div>
                <p className="text-sm text-[#5C5F66] mb-1">
                    Επιλέξτε χρωματικό σχέδιο. Η αλλαγή είναι άμεση και αποθηκεύεται τοπικά στη συσκευή σας.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {THEMES.map((t) => {
                    const selected = active === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => choose(t.key)}
                            data-testid={`theme-${t.key}`}
                            className={`group relative text-left bg-white border-2 transition-all duration-150 active:scale-[0.99] overflow-hidden ${
                                selected
                                    ? "border-[#002FA7] ring-4 ring-[#002FA7]/15"
                                    : "border-[#0A0A0A] hover:border-[#002FA7]"
                            }`}
                        >
                            {/* Swatch strip */}
                            <div className="grid grid-cols-4 h-14">
                                {t.swatches.map((c, i) => (
                                    <div
                                        key={i}
                                        style={{ backgroundColor: c }}
                                        className="border-r last:border-r-0 border-black/10"
                                    />
                                ))}
                            </div>
                            <div className="p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-heading text-2xl font-black tracking-tighter">
                                        {t.label}
                                    </span>
                                    {selected && (
                                        <span className="ml-auto inline-flex items-center gap-1 bg-[#002FA7] text-white text-[10px] font-black uppercase tracking-wider px-2 py-1">
                                            <Check size={12} weight="bold" /> ΕΝΕΡΓΟ
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-[#5C5F66]">{t.description}</p>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="border border-[#E2E4E9] bg-[#F4F5F7] p-4">
                <p className="text-xs text-[#5C5F66]">
                    Σημ.: Τα θέματα αλλάζουν τα χρώματα διεπαφής (φόντο, κάρτες, κουμπιά).
                    Δεν επηρεάζουν τη βάση δεδομένων ή τις λειτουργίες της εφαρμογής.
                </p>
            </div>
        </div>
    );
}

// ===== Logs Section =====
// Color palette for month accents (cycles)
const MONTH_COLORS = [
    { bar: "#002FA7", bg: "#EEF1FB", text: "#001D66" },
    { bar: "#34C759", bg: "#EAF7EE", text: "#1F8A3E" },
    { bar: "#FF9500", bg: "#FFF3E0", text: "#A65A00" },
    { bar: "#5856D6", bg: "#EFEEFB", text: "#2A2880" },
    { bar: "#FF3B30", bg: "#FDEAE8", text: "#B11919" },
    { bar: "#FFCC00", bg: "#FFF8DD", text: "#7A5C00" },
    { bar: "#0099A8", bg: "#E1F5F7", text: "#005C66" },
    { bar: "#AF52DE", bg: "#F6EDFB", text: "#5E2080" },
];
const colorForMonth = (key) => {
    if (!key) return MONTH_COLORS[0];
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return MONTH_COLORS[h % MONTH_COLORS.length];
};

// Map action keyword to icon background
const actionAccent = (action) => {
    const a = (action || "").toUpperCase();
    if (a.includes("ΔΙΑΓΡΑΦΗ") || a.includes("ΑΠΟΤΥΧΙΑ")) return "#FF3B30";
    if (a.includes("ΕΙΣΟΔΟΣ") || a.includes("ΝΕΑ ΠΩΛΗΣΗ")) return "#34C759";
    if (a.includes("ΟΡΙΣΜΟΣ") || a.includes("ΑΛΛΑΓΗ") || a.includes("ΕΠΕΞΕΡΓΑΣΙΑ"))
        return "#FF9500";
    if (a.includes("ΠΑΥΣΗ")) return "#FFCC00";
    if (a.includes("BACKUP") || a.includes("ΕΠΑΝΑΦΟΡΑ")) return "#5856D6";
    return "#002FA7";
};

function LogsSection() {
    const [data, setData] = useState({ rows: [], months_available: [], count: 0 });
    const [loading, setLoading] = useState(true);
    const [monthFilter, setMonthFilter] = useState("ALL");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 1000 };
            if (monthFilter !== "ALL") params.month = monthFilter;
            if (search.trim()) params.search = search.trim();
            const r = await api.get("/logs", { params });
            setData(r.data);
        } catch {
            toast.error("Αποτυχία φόρτωσης logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthFilter, search]);

    // Group rows by month_key (descending)
    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of data.rows) {
            const k = r.month_key || "UNKNOWN";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(r);
        }
        return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    }, [data.rows]);

    return (
        <div className="space-y-4" data-testid="logs-section">
            <div className="border-2 border-[#0A0A0A] bg-white p-4 space-y-3">
                <div className="flex items-center gap-3">
                    <ListBullets size={20} weight="bold" />
                    <span className="font-heading text-2xl font-black tracking-tighter">
                        ΗΜΕΡΟΛΟΓΙΟ ΕΝΕΡΓΕΙΩΝ
                    </span>
                    <span className="text-xs font-mono text-[#5C5F66] ml-2">
                        {data.count} εγγραφές
                    </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[260px] max-w-md">
                        <MagnifyingGlass
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                        />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") setSearch(searchInput);
                            }}
                            placeholder="Αναζήτηση σε ενέργεια ή λεπτομέρειες..."
                            data-testid="logs-search"
                            className="rounded-none h-10 pl-9 border-2 border-[#0A0A0A]"
                        />
                    </div>
                    <Button
                        onClick={() => setSearch(searchInput)}
                        data-testid="logs-search-btn"
                        className="rounded-none h-10 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold text-xs uppercase tracking-wider"
                    >
                        ΑΝΑΖΗΤΗΣΗ
                    </Button>
                    {(searchInput || search) && (
                        <Button
                            onClick={() => {
                                setSearchInput("");
                                setSearch("");
                            }}
                            variant="outline"
                            data-testid="logs-search-clear"
                            className="rounded-none h-10 border-[#0A0A0A] font-bold text-xs"
                        >
                            ΚΑΘΑΡΙΣΜΟΣ
                        </Button>
                    )}
                </div>
                {/* Month chips */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setMonthFilter("ALL")}
                        data-testid="logs-month-ALL"
                        className={`px-3 h-8 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                            monthFilter === "ALL"
                                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                : "bg-white text-[#5C5F66] border-[#E2E4E9] hover:border-[#0A0A0A]"
                        }`}
                    >
                        ΟΛΑ
                    </button>
                    {(data.months_available || []).map((m) => {
                        const c = colorForMonth(m);
                        const active = monthFilter === m;
                        return (
                            <button
                                key={m}
                                onClick={() => setMonthFilter(m)}
                                data-testid={`logs-month-${m}`}
                                className="px-3 h-8 text-xs font-bold uppercase tracking-wider border-2 transition-all"
                                style={{
                                    backgroundColor: active ? c.bar : c.bg,
                                    color: active ? "white" : c.text,
                                    borderColor: c.bar,
                                }}
                            >
                                {monthLabel(m)}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center text-sm text-[#5C5F66]">
                    Φόρτωση...
                </div>
            ) : grouped.length === 0 ? (
                <div className="p-12 text-center text-sm text-[#5C5F66] border border-[#E2E4E9] bg-white">
                    Δεν υπάρχουν εγγραφές για αυτά τα κριτήρια.
                </div>
            ) : (
                <div className="space-y-6">
                    {grouped.map(([monthKey, rows]) => {
                        const c = colorForMonth(monthKey);
                        return (
                            <div
                                key={monthKey}
                                className="border-2"
                                style={{ borderColor: c.bar }}
                                data-testid={`logs-group-${monthKey}`}
                            >
                                <div
                                    className="px-4 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: c.bar, color: "white" }}
                                >
                                    <span className="font-heading text-lg font-black tracking-tighter">
                                        {monthLabel(monthKey)}
                                    </span>
                                    <span className="text-xs font-mono opacity-90">
                                        {rows.length}{" "}
                                        {rows.length === 1 ? "εγγραφή" : "εγγραφές"}
                                    </span>
                                </div>
                                <div className="bg-white">
                                    {rows.map((r) => (
                                        <LogRow
                                            key={r.id}
                                            row={r}
                                            monthColor={c}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function LogRow({ row, monthColor }) {
    const accent = actionAccent(row.action);
    return (
        <div
            className="grid grid-cols-[120px_1fr_auto] gap-3 items-start border-b border-[#E2E4E9] last:border-b-0 px-4 py-3 hover:bg-[#FAFAFA]"
            style={{ borderLeft: `4px solid ${monthColor.bar}` }}
        >
            <div className="font-mono text-xs text-[#5C5F66]">
                {fmtDate(row.created_at)}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span
                        className="inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white"
                        style={{ backgroundColor: accent }}
                    >
                        {row.action}
                    </span>
                </div>
                {row.details && (
                    <div className="text-sm text-[#0A0A0A] break-words">
                        {row.details}
                    </div>
                )}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F66] whitespace-nowrap">
                {row.user}
            </div>
        </div>
    );
}



// ============================================================
// USERS MANAGEMENT SECTION (RBAC)
// ============================================================
function UsersSection() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        username: "",
        password: "",
        name: "",
        role: "user",
    });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Αδυναμία φόρτωσης χρηστών"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setEditing(null);
        setForm({ username: "", password: "", name: "", role: "user" });
        setDialogOpen(true);
    };

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            username: u.username || "",
            password: "",
            name: u.name || "",
            role: u.role || "user",
        });
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.username.trim() || !form.name.trim()) {
            toast.error("Συμπληρώστε όνομα χρήστη και πλήρες όνομα");
            return;
        }
        if (!editing && !form.password.trim()) {
            toast.error("Ο κωδικός είναι υποχρεωτικός για νέο χρήστη");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const payload = {
                    username: form.username.trim(),
                    name: form.name.trim(),
                    role: form.role,
                };
                if (form.password.trim()) payload.password = form.password;
                await api.put(`/users/${editing.id}`, payload);
                toast.success("Χρήστης ενημερώθηκε");
            } else {
                await api.post("/users", {
                    username: form.username.trim(),
                    password: form.password,
                    name: form.name.trim(),
                    role: form.role,
                });
                toast.success("Ο χρήστης δημιουργήθηκε");
            }
            setDialogOpen(false);
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία αποθήκευσης");
        } finally {
            setSaving(false);
        }
    };

    const togglePause = async (u) => {
        try {
            await api.post(`/users/${u.id}/pause`, { paused: !u.paused });
            toast.success(
                !u.paused ? "Ο χρήστης τέθηκε σε παύση" : "Ο χρήστης ενεργοποιήθηκε"
            );
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα");
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/users/${confirmDelete.id}`);
            toast.success("Ο χρήστης διαγράφηκε");
            setConfirmDelete(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία διαγραφής");
        }
    };

    const roleBadge = (r) => {
        const map = {
            admin: { bg: "#FF3B30", label: "ΔΙΑΧΕΙΡΙΣΤΗΣ" },
            manager: { bg: "#5856D6", label: "ΥΠΕΥΘΥΝΟΣ" },
            user: { bg: "#34C759", label: "ΧΡΗΣΤΗΣ" },
        };
        const m = map[r] || map.user;
        return (
            <span
                className="inline-block px-2 py-0.5 text-[10px] font-black tracking-wider text-white"
                style={{ backgroundColor: m.bg }}
            >
                {m.label}
            </span>
        );
    };

    return (
        <div className="space-y-6" data-testid="users-section">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-1">
                        ΡΟΛΟΙ ΚΑΙ ΠΡΟΣΒΑΣΗ
                    </div>
                    <h2 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">
                        ΧΡΗΣΤΕΣ ΣΥΣΤΗΜΑΤΟΣ
                    </h2>
                    <p className="mt-1 text-sm text-[#5C5F66]">
                        Διαχειριστής: πλήρη δικαιώματα · Υπεύθυνος: διαχείριση
                        ρυθμίσεων · Χρήστης: μόνο πωλήσεις
                    </p>
                </div>
                <Button
                    onClick={openNew}
                    data-testid="add-user-button"
                    className="rounded-none h-11 px-5 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold tracking-wide"
                >
                    ΝΕΟΣ ΧΡΗΣΤΗΣ
                </Button>
            </div>

            <div className="border-2 border-[#0A0A0A] bg-white">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F4F5F7] border-b-2 border-[#0A0A0A]">
                            <TableHead className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                                ΟΝΟΜΑ ΧΡΗΣΤΗ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                                ΠΛΗΡΕΣ ΟΝΟΜΑ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                                ΡΟΛΟΣ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                                ΚΑΤΑΣΤΑΣΗ
                            </TableHead>
                            <TableHead className="text-right text-[10px] font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                                ΕΝΕΡΓΕΙΕΣ
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-sm text-[#5C5F66] py-6">
                                    Φόρτωση...
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && users.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-sm text-[#5C5F66] py-6">
                                    Δεν υπάρχουν χρήστες. Δημιουργήστε τον πρώτο.
                                </TableCell>
                            </TableRow>
                        )}
                        {users.map((u) => (
                            <TableRow
                                key={u.id}
                                data-testid={`user-row-${u.username}`}
                                className={u.paused ? "bg-[#FFF8E1]" : ""}
                            >
                                <TableCell className="font-mono text-sm font-bold text-[#0A0A0A]">
                                    {u.username}
                                </TableCell>
                                <TableCell className="text-sm text-[#0A0A0A]">
                                    {u.name}
                                </TableCell>
                                <TableCell>{roleBadge(u.role)}</TableCell>
                                <TableCell>
                                    {u.paused ? (
                                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#FF9500] text-white">
                                            ΣΕ ΠΑΥΣΗ
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#34C759] text-white">
                                            ΕΝΕΡΓΟΣ
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <button
                                            onClick={() => togglePause(u)}
                                            data-testid={`toggle-user-pause-${u.username}`}
                                            className={`p-2 transition-colors ${
                                                u.paused
                                                    ? "text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white"
                                                    : "text-[#FF9500] hover:bg-[#FF9500] hover:text-white"
                                            }`}
                                            title={u.paused ? "Ενεργοποίηση" : "Παύση"}
                                        >
                                            {u.paused ? (
                                                <PlayCircle size={18} weight="bold" />
                                            ) : (
                                                <Clock size={18} weight="bold" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => openEdit(u)}
                                            data-testid={`edit-user-${u.username}`}
                                            className="p-2 hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] transition-colors text-[11px] font-bold"
                                        >
                                            ΕΠΕΞΕΡΓΑΣΙΑ
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(u)}
                                            data-testid={`delete-user-${u.username}`}
                                            className="p-2 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] transition-colors"
                                        >
                                            <Trash size={16} weight="bold" />
                                        </button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className="rounded-none max-w-lg border-2 border-[#0A0A0A]"
                    data-testid="user-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                            {editing ? "ΕΠΕΞΕΡΓΑΣΙΑ ΧΡΗΣΤΗ" : "ΝΕΟΣ ΧΡΗΣΤΗΣ"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#5C5F66]">
                            {editing
                                ? "Αφήστε τον κωδικό κενό για να μην αλλάξει."
                                : "Ο κωδικός είναι υποχρεωτικός."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4 mt-2">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                ΟΝΟΜΑ ΧΡΗΣΤΗ (USERNAME)
                            </Label>
                            <Input
                                value={form.username}
                                onChange={(e) =>
                                    setForm({ ...form, username: e.target.value })
                                }
                                data-testid="user-username-input"
                                className="rounded-none h-11 border-[#E2E4E9]"
                                placeholder="π.χ. maria"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                ΠΛΗΡΕΣ ΟΝΟΜΑ
                            </Label>
                            <Input
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                data-testid="user-name-input"
                                className="rounded-none h-11 border-[#E2E4E9]"
                                placeholder="π.χ. Μαρία Παπαδοπούλου"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                {editing ? "ΝΕΟΣ ΚΩΔΙΚΟΣ (ΠΡΟΑΙΡΕΤΙΚΑ)" : "ΚΩΔΙΚΟΣ"}
                            </Label>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={(e) =>
                                    setForm({ ...form, password: e.target.value })
                                }
                                data-testid="user-password-input"
                                className="rounded-none h-11 border-[#E2E4E9] font-mono"
                                placeholder={editing ? "••••" : "Υποχρεωτικός"}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                ΡΟΛΟΣ
                            </Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v })}
                            >
                                <SelectTrigger
                                    className="rounded-none h-11 border-[#E2E4E9]"
                                    data-testid="user-role-select"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="user" data-testid="role-user">
                                        ΧΡΗΣΤΗΣ — μόνο πωλήσεις
                                    </SelectItem>
                                    <SelectItem value="manager" data-testid="role-manager">
                                        ΥΠΕΥΘΥΝΟΣ — διαχείριση
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-[#5C5F66]">
                                Ο ρόλος «Διαχειριστής» δεν μπορεί να ανατεθεί εδώ.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={() => setDialogOpen(false)}
                                variant="outline"
                                data-testid="user-cancel-button"
                                className="rounded-none h-11 border-2 border-[#0A0A0A] font-bold"
                            >
                                ΑΚΥΡΩΣΗ
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="user-save-button"
                                className="rounded-none h-11 bg-[#002FA7] hover:bg-[#001D66] text-white font-black tracking-wider"
                            >
                                {saving
                                    ? "ΑΠΟΘΗΚΕΥΣΗ..."
                                    : editing
                                    ? "ΑΠΟΘΗΚΕΥΣΗ"
                                    : "ΔΗΜΙΟΥΡΓΙΑ"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent
                    className="rounded-none max-w-md border-2 border-[#FF3B30]"
                    data-testid="delete-user-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                            ΔΙΑΓΡΑΦΗ ΧΡΗΣΤΗ
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#5C5F66]">
                            Θέλετε σίγουρα να διαγράψετε τον χρήστη{" "}
                            <strong>{confirmDelete?.name}</strong> (
                            {confirmDelete?.username});
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => setConfirmDelete(null)}
                            variant="outline"
                            className="rounded-none h-11 border-2 border-[#0A0A0A] font-bold"
                        >
                            ΑΚΥΡΩΣΗ
                        </Button>
                        <Button
                            type="button"
                            onClick={handleDelete}
                            data-testid="confirm-delete-user"
                            className="rounded-none h-11 bg-[#FF3B30] hover:bg-[#D72D24] text-white font-black tracking-wider"
                        >
                            ΔΙΑΓΡΑΦΗ
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// ===== License Management (Administrator only) =====
function LicenseSection() {
    const [license, setLicense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState("");
    const [confirm, setConfirm] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/license");
            setLicense(data);
            if (data?.is_set) setDays(String(data.days_remaining));
        } catch {
            toast.error("Αποτυχία φόρτωσης άδειας");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const n = parseInt(days, 10);
        if (Number.isNaN(n) || n < 0) {
            toast.error("Δώστε έγκυρο μη αρνητικό αριθμό ημερών");
            return;
        }
        if (n > 100000) {
            toast.error("Παράλογα μεγάλος αριθμός");
            return;
        }
        setConfirm(true);
    };

    const handleConfirm = async (code) => {
        try {
            const n = parseInt(days, 10);
            const { data } = await api.put("/license", {
                days_remaining: n,
                confirm_code: code,
            });
            setLicense(data);
            toast.success(`Άδεια ορίστηκε σε ${n} ημέρες`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα");
            throw err;
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-sm text-[#5C5F66]">
                Φόρτωση...
            </div>
        );
    }

    const sev = license?.severity || "ok";
    const sevStyles = {
        ok: { bg: "bg-[#34C759]", border: "border-[#1F8A3E]", text: "text-white" },
        warn: { bg: "bg-[#FF9500]", border: "border-[#C76F00]", text: "text-white" },
        urgent: { bg: "bg-[#FF3B30]", border: "border-[#A4291F]", text: "text-white" },
        expired: { bg: "bg-[#0A0A0A]", border: "border-[#FF3B30]", text: "text-white" },
    };
    const s = sevStyles[sev] || sevStyles.ok;

    return (
        <div className="space-y-6 max-w-3xl" data-testid="license-section">
            <div className="border-2 border-[#FF3B30] bg-white">
                <div className="bg-[#FF3B30] text-white px-4 py-2 flex items-center gap-2">
                    <Key size={16} weight="bold" />
                    <span className="text-sm font-bold uppercase tracking-[0.2em]">
                        ΑΔΕΙΑ ΛΕΙΤΟΥΡΓΙΑΣ
                    </span>
                </div>
                <div className="p-6 space-y-5">
                    <p className="text-sm text-[#5C5F66] leading-relaxed">
                        Η άδεια λειτουργίας μειώνεται αυτόματα κατά μία ημέρα στις
                        00:00 (UTC). Όταν φτάσει στο 0, μόνο ο διαχειριστής μπορεί να
                        συνδεθεί. Ορίστε εδώ τις ημέρες που απομένουν.
                    </p>

                    {/* Current status card */}
                    {license?.is_set ? (
                        <div
                            className={`${s.bg} ${s.text} border-4 ${s.border} px-5 py-4`}
                            data-testid="license-current-status"
                            data-severity={sev}
                        >
                            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                <div className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-90">
                                    {sev === "expired" ? "ΛΗΞΕ" : "ΤΡΕΧΟΥΣΑ ΑΔΕΙΑ"}
                                </div>
                                <div className="font-heading font-black tracking-tighter text-3xl leading-none tabular-nums">
                                    {Math.max(0, license.days_remaining)}
                                    <span className="text-sm font-bold ml-2 opacity-90">
                                        {sev === "expired"
                                            ? "ΗΜΕΡΕΣ"
                                            : "ΗΜΕΡΕΣ ΛΕΙΤΟΥΡΓΙΑΣ"}
                                    </span>
                                </div>
                            </div>
                            {license.set_at && (
                                <div className="mt-2 text-xs font-mono opacity-80 border-t border-white/30 pt-2">
                                    Τελευταίος ορισμός: {fmtDate(license.set_at)}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-[#5C5F66] bg-[#F4F5F7] p-4 text-sm text-[#5C5F66]">
                            <Warning
                                size={16}
                                weight="bold"
                                className="inline mr-2 align-text-bottom"
                            />
                            Δεν έχει οριστεί άδεια λειτουργίας. Όλοι οι χρήστες μπορούν
                            να συνδεθούν.
                        </div>
                    )}

                    {/* Set/Update form */}
                    <form
                        onSubmit={handleSubmit}
                        className="space-y-3 border-t border-[#E2E4E9] pt-5"
                    >
                        <Label
                            htmlFor="license-days"
                            className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]"
                        >
                            Νέες ημέρες λειτουργίας
                        </Label>
                        <div className="flex items-center gap-3">
                            <Input
                                id="license-days"
                                type="number"
                                min="0"
                                max="100000"
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                                placeholder="π.χ. 100"
                                data-testid="license-days-input"
                                className="rounded-none h-12 w-40 border-2 border-[#0A0A0A] font-mono text-lg"
                            />
                            <Button
                                type="submit"
                                data-testid="license-submit"
                                className="rounded-none h-12 bg-[#FF3B30] hover:bg-[#A4291F] text-white font-bold uppercase tracking-wider"
                            >
                                <Key size={14} weight="bold" className="mr-2" />
                                Ορισμος
                            </Button>
                        </div>
                        <div className="text-[11px] text-[#5C5F66] font-mono">
                            🟢 ≥30 ημέρες · 🟠 &lt;30 ημέρες · 🔴 &lt;10 ημέρες · ⚫
                            ληγμένη
                        </div>
                    </form>
                </div>
            </div>

            <ConfirmCodeDialog
                open={confirm}
                onOpenChange={setConfirm}
                title="Επιβεβαίωση ορισμού άδειας"
                description={`Πρόκειται να ορίσετε την άδεια σε ${days} ημέρες. Εισάγετε τον κωδικό επιβεβαίωσης.`}
                onConfirm={handleConfirm}
            />
        </div>
    );
}



// ===== Database Statistics (Administrator only) =====
const COLLECTION_LABELS = {
    customers: "Πελάτες",
    items: "Είδη",
    sales: "Πωλήσεις",
    transactions: "Κινήσεις",
    parked_sales: "Σε αναστολή",
    monthly_limits: "Μηνιαία όρια",
    audit_logs: "Logs",
    backups: "Backups",
    settings: "Ρυθμίσεις",
    users: "Χρήστες",
};

function DbStatsSection() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [quotaInput, setQuotaInput] = useState("");
    const [confirm, setConfirm] = useState(null);
    const [compacting, setCompacting] = useState(false);

    const load = async (silent = false) => {
        if (silent) setRefreshing(true);
        else setLoading(true);
        try {
            const { data } = await api.get("/db-stats");
            setStats(data);
            setQuotaInput(String(data.quota_mb));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία φόρτωσης");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleQuotaSubmit = (e) => {
        e.preventDefault();
        const n = parseInt(quotaInput, 10);
        if (Number.isNaN(n) || n < 1 || n > 1_000_000) {
            toast.error("Δώστε έγκυρη τιμή σε MB (1..1.000.000)");
            return;
        }
        setConfirm("quota");
    };

    const saveQuota = async (code) => {
        const n = parseInt(quotaInput, 10);
        const { data } = await api.put("/db-stats/quota", {
            quota_mb: n,
            confirm_code: code,
        });
        setStats(data);
        toast.success(`Όριο πακέτου: ${n} MB`);
    };

    const runCompact = async (code) => {
        setCompacting(true);
        try {
            const { data } = await api.post("/db-stats/compact", { confirm_code: code });
            setStats(data.stats);
            setQuotaInput(String(data.stats.quota_mb));
            if (data.error) toast.error(`Ο server δεν επέτρεψε την απελευθέρωση: ${data.error}`);
            else if (data.freed_bytes > 0) toast.success(`Απελευθερώθηκαν ${fmtBytes(data.freed_bytes)}`);
            else toast.info("Δεν υπήρχε χώρος για απελευθέρωση αυτή τη στιγμή");
        } finally {
            setCompacting(false);
        }
    };

    const handleConfirm = async (code) => {
        const mode = confirm;
        setConfirm(null);
        try {
            if (mode === "compact") await runCompact(code);
            else await saveQuota(code);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα");
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-sm text-[#5C5F66]">
                Φόρτωση...
            </div>
        );
    }
    if (!stats) return null;

    const sevColor = {
        ok: { bg: "bg-[#34C759]", border: "border-[#1F8A3E]", txt: "text-[#1F8A3E]", bar: "bg-[#34C759]" },
        warn: { bg: "bg-[#FF9500]", border: "border-[#C76F00]", txt: "text-[#C76F00]", bar: "bg-[#FF9500]" },
        urgent: { bg: "bg-[#FF3B30]", border: "border-[#A4291F]", txt: "text-[#A4291F]", bar: "bg-[#FF3B30]" },
        over: { bg: "bg-[#0A0A0A]", border: "border-[#FF3B30]", txt: "text-[#0A0A0A]", bar: "bg-[#FF3B30]" },
    }[stats.severity] || {
        bg: "bg-[#5856D6]",
        border: "border-[#3F3DA0]",
        txt: "text-[#3F3DA0]",
        bar: "bg-[#5856D6]",
    };

    const usedClamped = Math.min(100, stats.used_pct);

    // Sort collections desc by storage_size_bytes for display
    const cols = [...stats.collections].sort(
        (a, b) => (b.storage_size_bytes + b.index_size_bytes) - (a.storage_size_bytes + a.index_size_bytes)
    );
    const maxColTotal = Math.max(
        1,
        ...cols.map((c) => c.storage_size_bytes + c.index_size_bytes)
    );

    return (
        <div className="space-y-6 max-w-4xl" data-testid="db-stats-section">
            {/* Header card with usage bar */}
            <div className="border-2 border-[#5856D6] bg-white">
                <div className="bg-[#5856D6] text-white px-4 py-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <HardDrives size={16} weight="bold" />
                        <span className="text-sm font-bold uppercase tracking-[0.2em]">
                            ΣΤΑΤΙΣΤΙΚΑ ΒΑΣΗΣ
                        </span>
                    </div>
                    <Button
                        type="button"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        data-testid="db-stats-refresh"
                        className="rounded-none h-8 px-3 bg-white text-[#5856D6] hover:bg-[#F4F5F7] font-bold text-xs border-0"
                    >
                        <ArrowCounterClockwise size={14} weight="bold" className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
                        ΑΝΑΝΕΩΣΗ
                    </Button>
                </div>
                <div className="p-6 space-y-5">
                    {/* Big numeric summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="border-2 border-[#5856D6] bg-[#F0EFFD] p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5856D6]">
                                Χρησιμοποιείται
                            </div>
                            <div
                                className="font-heading font-black tracking-tighter text-3xl text-[#0A0A0A]"
                                data-testid="db-stats-used"
                            >
                                {fmtBytes(stats.total_size_bytes)}
                            </div>
                            <div className="text-xs text-[#5C5F66] font-mono mt-1">
                                από {stats.quota_mb} MB
                            </div>
                        </div>
                        <div className={`border-2 ${sevColor.border} bg-white p-4`}>
                            <div className={`text-[10px] font-bold uppercase tracking-[0.15em] ${sevColor.txt}`}>
                                Ποσοστό χρήσης
                            </div>
                            <div className="font-heading font-black tracking-tighter text-3xl text-[#0A0A0A]" data-testid="db-stats-pct">
                                {stats.used_pct.toFixed(2)}%
                            </div>
                            <div className="text-xs text-[#5C5F66] font-mono mt-1">
                                Κατάσταση:{" "}
                                <span className={`font-bold ${sevColor.txt}`}>
                                    {stats.severity === "ok" && "✓ Καλή"}
                                    {stats.severity === "warn" && "⚠ Προσοχή"}
                                    {stats.severity === "urgent" && "⚠ Κρίσιμη"}
                                    {stats.severity === "over" && "✕ Υπέρβαση"}
                                </span>
                            </div>
                        </div>
                        <div className="border-2 border-[#34C759] bg-[#EAF7EE] p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1F8A3E]">
                                Διαθέσιμος χώρος
                            </div>
                            <div className="font-heading font-black tracking-tighter text-3xl text-[#0A0A0A]" data-testid="db-stats-free">
                                {fmtBytes(stats.free_bytes)}
                            </div>
                            <div className="text-xs text-[#5C5F66] font-mono mt-1">
                                σύνολο πακέτου: {stats.quota_mb} MB
                            </div>
                        </div>
                        <div className="border-2 border-[#0A0A0A] bg-white p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                Σύνολο εγγραφών
                            </div>
                            <div className="font-heading font-black tracking-tighter text-3xl text-[#0A0A0A]" data-testid="db-stats-docs">
                                {stats.documents_count.toLocaleString("el-GR")}
                            </div>
                            <div className="text-xs text-[#5C5F66] font-mono mt-1">
                                σε {stats.collections_count} πίνακες
                            </div>
                        </div>
                    </div>

                    {/* Big progress bar */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                            <span>Χρήση χώρου ως προς το πακέτο</span>
                            <span className={`${sevColor.txt} font-mono`}>
                                {stats.used_pct.toFixed(2)}%
                            </span>
                        </div>
                        <div className="relative h-7 bg-[#F0EFFD] border-2 border-[#5856D6] overflow-hidden">
                            <div
                                className={`absolute top-0 left-0 h-full ${sevColor.bar} transition-all duration-500`}
                                style={{ width: `${usedClamped}%` }}
                                data-testid="db-stats-progress"
                            />
                            {/* 75% / 90% threshold ticks */}
                            <div className="absolute top-0 h-full w-px bg-[#FF9500] opacity-60" style={{ left: "75%" }} />
                            <div className="absolute top-0 h-full w-px bg-[#FF3B30] opacity-60" style={{ left: "90%" }} />
                        </div>
                        <div className="text-[10px] text-[#5C5F66] mt-1 font-mono">
                            🟢 0–75% · 🟠 75–90% · 🔴 90–100% · ⚫ &gt;100% (υπέρβαση)
                        </div>
                    </div>

                    {/* Detailed breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs font-mono text-[#5C5F66]">
                        <div className="bg-[#FFF4E5] border border-[#FF9500] p-3" data-testid="db-stats-reclaimable">
                            <div className="font-bold uppercase tracking-wider text-[#C76F00] text-[10px]">
                                Κενός (ανακτήσιμος)
                            </div>
                            <div className="text-base font-bold text-[#0A0A0A] mt-1">{fmtBytes(stats.reclaimable_bytes)}</div>
                        </div>
                        <div className="bg-[#F4F5F7] border border-[#E2E4E9] p-3">
                            <div className="font-bold uppercase tracking-wider text-[#0A0A0A] text-[10px]">
                                Δεδομένα
                            </div>
                            <div className="text-base font-bold text-[#0A0A0A] mt-1">{fmtBytes(stats.data_size_bytes)}</div>
                        </div>
                        <div className="bg-[#F4F5F7] border border-[#E2E4E9] p-3">
                            <div className="font-bold uppercase tracking-wider text-[#0A0A0A] text-[10px]">
                                Storage
                            </div>
                            <div className="text-base font-bold text-[#0A0A0A] mt-1">{fmtBytes(stats.storage_size_bytes)}</div>
                        </div>
                        <div className="bg-[#F4F5F7] border border-[#E2E4E9] p-3">
                            <div className="font-bold uppercase tracking-wider text-[#0A0A0A] text-[10px]">
                                Indexes
                            </div>
                            <div className="text-base font-bold text-[#0A0A0A] mt-1">{fmtBytes(stats.index_size_bytes)}</div>
                        </div>
                    </div>

                    <CompactPanel
                        reclaimable={stats.reclaimable_bytes}
                        busy={compacting}
                        onClick={() => setConfirm("compact")}
                    />
                </div>
            </div>

            {/* Per-collection breakdown */}
            <div className="border-2 border-[#0A0A0A] bg-white">
                <div className="bg-[#0A0A0A] text-white px-4 py-2 flex items-center gap-2">
                    <Database size={16} weight="bold" />
                    <span className="text-sm font-bold uppercase tracking-[0.2em]">
                        ΑΝΑΛΥΣΗ ΑΝΑ ΠΙΝΑΚΑ
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-b-2 border-[#0A0A0A] hover:bg-transparent">
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10">
                                    Πίνακας
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10 text-right">
                                    Εγγραφές
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10 text-right">
                                    Δεδομένα
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10 text-right">
                                    Storage
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10 text-right">
                                    Indexes
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#C76F00] h-10 text-right">
                                    Κενός
                                </TableHead>
                                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-10 w-[140px]">
                                    Σύνολο
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {cols.map((c) => {
                                const total = c.storage_size_bytes + c.index_size_bytes;
                                const pct = (total / maxColTotal) * 100;
                                return (
                                    <TableRow
                                        key={c.name}
                                        className="border-b border-[#E2E4E9] hover:bg-[#F4F5F7]"
                                        data-testid={`db-row-${c.name}`}
                                    >
                                        <TableCell className="py-2.5">
                                            <div className="font-bold text-[#0A0A0A]">
                                                {COLLECTION_LABELS[c.name] || c.name}
                                            </div>
                                            <div className="text-[10px] text-[#5C5F66] font-mono">
                                                {c.name}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm font-bold">
                                            {c.count.toLocaleString("el-GR")}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                            {fmtBytes(c.data_size_bytes)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                            {fmtBytes(c.storage_size_bytes)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                            {fmtBytes(c.index_size_bytes)}
                                        </TableCell>
                                        <TableCell
                                            className="text-right font-mono text-xs text-[#C76F00] font-bold"
                                            data-testid={`db-free-${c.name}`}
                                        >
                                            {fmtBytes(c.free_storage_bytes || 0)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-3 bg-[#F4F5F7] border border-[#E2E4E9] relative overflow-hidden">
                                                    <div
                                                        className="absolute left-0 top-0 h-full bg-[#5856D6]"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="font-mono text-[11px] font-bold text-[#0A0A0A] shrink-0 w-[60px] text-right">
                                                    {fmtBytes(total)}
                                                </span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Quota config form */}
            <div className="border-2 border-[#0A0A0A] bg-white">
                <div className="bg-[#0A0A0A] text-white px-4 py-2 flex items-center gap-2">
                    <Key size={16} weight="bold" />
                    <span className="text-sm font-bold uppercase tracking-[0.2em]">
                        ΟΡΙΟ ΠΑΚΕΤΟΥ ΦΙΛΟΞΕΝΙΑΣ
                    </span>
                </div>
                <form onSubmit={handleQuotaSubmit} className="p-6 space-y-4">
                    <p className="text-sm text-[#5C5F66] leading-relaxed">
                        Ορίστε τον χώρο σε MB που έχετε αγοράσει από τον πάροχο
                        φιλοξενίας. Ο διαθέσιμος χώρος υπολογίζεται με βάση αυτή
                        την τιμή.
                    </p>
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
                                Όριο σε MB
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                step={1}
                                value={quotaInput}
                                onChange={(e) => setQuotaInput(e.target.value)}
                                data-testid="db-quota-input"
                                className="rounded-none h-12 text-xl font-bold font-mono border-2 border-[#0A0A0A]"
                                placeholder="π.χ. 512"
                            />
                            <div className="text-[10px] text-[#5C5F66] mt-1 font-mono">
                                Συνηθισμένα πακέτα: 256 / 512 / 1024 / 2048 / 5120 MB
                            </div>
                        </div>
                        <Button
                            type="submit"
                            data-testid="db-quota-save"
                            className="rounded-none h-12 px-6 bg-[#5856D6] hover:bg-[#3F3DA0] text-white font-bold tracking-wider"
                        >
                            ΑΠΟΘΗΚΕΥΣΗ
                        </Button>
                    </div>
                </form>
            </div>

            <ConfirmCodeDialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm === "compact" ? "Απελευθέρωση χώρου βάσης" : "Επιβεβαίωση ορίου πακέτου"}
                description={
                    confirm === "compact"
                        ? "Θα αποδεσμευτεί ο κενός χώρος από όλους τους πίνακες. Τα δεδομένα δεν αλλάζουν. Καλύτερα να γίνει όταν δεν γίνονται πωλήσεις. Εισάγετε τον κωδικό επιβεβαίωσης."
                        : `Πρόκειται να ορίσετε το πακέτο φιλοξενίας σε ${quotaInput} MB. Εισάγετε τον κωδικό επιβεβαίωσης.`
                }
                onConfirm={handleConfirm}
            />
        </div>
    );
}

function CompactPanel({ reclaimable, busy, onClick }) {
    return (
        <div className="border-2 border-[#FF9500] bg-[#FFF9F0] p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="db-compact-panel">
            <p className="text-xs text-[#5C5F66] leading-relaxed max-w-xl">
                Όταν διαγράφονται ή αντικαθίστανται εγγραφές (π.χ. παλιά backup), η βάση κρατά τον χώρο
                δεσμευμένο ως <strong>κενό</strong> για μελλοντική χρήση. Γι' αυτό ο χώρος στον δίσκο είναι
                μεγαλύτερος από τα πραγματικά δεδομένα. Με την απελευθέρωση ο κενός χώρος επιστρέφει στο πακέτο.
            </p>
            <Button
                type="button"
                onClick={onClick}
                disabled={busy}
                data-testid="db-compact-btn"
                className="rounded-none h-12 px-5 bg-[#FF9500] hover:bg-[#C76F00] text-white font-bold tracking-wider"
            >
                <Broom size={18} weight="bold" className={`mr-2 ${busy ? "animate-pulse" : ""}`} />
                {busy ? "ΑΠΕΛΕΥΘΕΡΩΣΗ..." : `ΑΠΕΛΕΥΘΕΡΩΣΗ ΧΩΡΟΥ (${fmtBytes(reclaimable)})`}
            </Button>
        </div>
    );
}


